import { motion } from "framer-motion";
import { Droplets, Bandage, Activity, ArrowRight } from "lucide-react";
import { IMAGES, PRACTICE } from "@/data/practice";

const services = [
  {
    id: "perfusions",
    title: "Perfusions",
    icon: Droplets,
    image: IMAGES.perfusions,
    description:
      "Pose et surveillance de perfusions à domicile : antibiothérapie, hydratation, traitements spécifiques. Un suivi rigoureux et sécurisé.",
    points: ["Antibiothérapie", "Réhydratation", "Chimiothérapie de soutien"],
  },
  {
    id: "pansements",
    title: "Pansements",
    icon: Bandage,
    image: IMAGES.pansements,
    description:
      "Réfection de pansements simples et complexes, soins de plaies, escarres, ulcères et cicatrisation post-opératoire.",
    points: ["Plaies chroniques", "Escarres & ulcères", "Post-opératoire"],
  },
  {
    id: "diabete",
    title: "Prise en charge du diabète",
    icon: Activity,
    image:
      "https://images.unsplash.com/photo-1684156486280-ff59f07fc5bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHxwYXRpZW50JTIwYmxvb2QlMjBnbHVjb3NlJTIwdGVzdCUyMGZpbmdlciUyMGdsdWNvbWV0ZXIlMjBkaWFiZXRlc3xlbnwwfHx8fDE3ODQwNDE3MDB8MA&ixlib=rb-4.1.0&q=85",
    description:
      "Accompagnement des patients diabétiques : injections d'insuline, surveillance glycémique et éducation thérapeutique.",
    points: ["Injections d'insuline", "Surveillance glycémique", "Éducation & conseils"],
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const PriorityServices = () => {
  return (
    <section
      id="services"
      data-testid="priority-services-section"
      className="py-20 sm:py-28 bg-[#F0EFEB] relative grain-overlay"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 relative">
        <div className="max-w-2xl mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#3F9E8E] mb-4">
            Nos soins prioritaires
          </p>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#2C362B] leading-tight">
            Des soins techniques, avec humanité
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#4A554A] leading-relaxed">
            Notre cabinet met son expertise au service de votre santé, à domicile
            comme au cabinet.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {services.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.id}
                data-testid={`service-card-${s.id}`}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                custom={i}
                whileHover={{ y: -6 }}
                className="group bg-white rounded-2xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-[#E0E0D8] flex flex-col transition-shadow duration-300 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)]"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={s.image}
                    alt={`${s.title} - soins infirmiers à Bordeaux`}
                    className="w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2C362B]/50 to-transparent" />
                  <span className="absolute top-4 left-4 grid place-items-center w-11 h-11 rounded-xl bg-white/90 backdrop-blur text-[#3F9E8E] shadow-sm">
                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                  </span>
                </div>

                <div className="p-7 flex flex-col flex-1">
                  <h3 className="font-display font-semibold text-xl sm:text-2xl text-[#2C362B]">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#4A554A] leading-relaxed">
                    {s.description}
                  </p>
                  <ul className="mt-5 space-y-2 flex-1">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-center gap-2.5 text-sm text-[#2C362B]">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#6B7A66]" />
                        {p}
                      </li>
                    ))}
                  </ul>
                  <a
                    href={`tel:${PRACTICE.phoneRaw}`}
                    data-testid={`service-cta-${s.id}`}
                    className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#3F9E8E] hover:gap-2.5 transition-[gap] duration-200"
                  >
                    Prendre rendez-vous
                    <ArrowRight className="w-4 h-4" strokeWidth={2} />
                  </a>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
